/**
 * Media Management - Moon Moon Agency
 * Handles video lazy loading with viewport-based logic
 */

/*********************************************************************************************
 * 🎥 VIDEO MODULE
 */
const VideoManager = {
	// Local configuration
	config: {
		selectors: {
			videoLazy: '.mm-video-lazy',
			videoSource: 'source'
		},
		classes: {
			videoLoaded: 'mm-video-loaded'
		},
		timing: {
			resizeDebounce: 150
		}
	},

	// Stored resize handler reference for proper cleanup
	_resizeHandler: null,

	/**
	 * Initialize lazy video loading
	 */
	init() {
		const videos = window.getElements(this.config.selectors.videoLazy);
		if (!videos.length) return;

		const loadVideo = (video) => {
			if (video.dataset.loaded !== 'true') {
				const source = video.querySelector(this.config.selectors.videoSource);
				if (!source?.dataset.src) return;

				source.src = source.dataset.src;
				video.preload = 'metadata';
				video.dataset.loaded = 'true';

				video.load();
				video.play().catch((err) =>
					window.log('warn', 'VideoManager', 'Video auto-play failed', { error: err.message })
				);
			}
		};

		const unloadVideo = (video) => {
			if (video.dataset.loaded === 'true') {
				video.pause();
				video.currentTime = 0;
				// Clear source to release memory
				const source = video.querySelector(this.config.selectors.videoSource);
				if (source) {
					source.src = '';
				}
				video.load();
				video.dataset.loaded = 'false';
			}
		};

		const handleVideoVisibility = (video) => {
			const isMobileView = window.matchMedia(`(max-width: ${window.MOBILE_BREAKPOINT}px)`).matches;
			const isDesktop = video.classList.contains('mm-desktop');
			const isMobileVideo = video.classList.contains('mm-mobile');

			const shouldPlay = (!isDesktop && !isMobileVideo) ||
								(isDesktop && !isMobileView) ||
								(isMobileVideo && isMobileView);

			if (shouldPlay) {
				loadVideo(video);
				video.classList.add(this.config.classes.videoLoaded);
			} else {
				video.classList.remove(this.config.classes.videoLoaded);
			}
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleVideoVisibility(entry.target);
				} else {
					unloadVideo(entry.target);
				}
			});
		}, {
			rootMargin: '50px 0px',
			threshold: 0.1
		});

		videos.forEach((video) => observer.observe(video));

		// Resize handling — remove previous handler before adding new one to avoid accumulation
		if (this._resizeHandler) {
			window.removeEventListener('resize', this._resizeHandler);
		}
		this._resizeHandler = window.debounce(() => {
			videos.forEach((video) => {
				if (video.classList.contains(this.config.classes.videoLoaded)) {
					handleVideoVisibility(video);
				}
			});
		}, this.config.timing.resizeDebounce);

		window.addEventListener('resize', this._resizeHandler);

		window.emit('global:video-manager-initialized');
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.VideoManager = VideoManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmInitVideoLazy = () => VideoManager.init();

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	VideoManager.init();
});
